/**
 * The three cross-primitive treatments, each defined once.
 *
 * A treatment is a decision that has to be identical everywhere or it stops being a system: how a
 * focused control announces itself, how a surface sits above the page, and what a card does when a
 * pointer is over it. The prior audit found the last one done three different ways in one app —
 * `PromoCard3D` stacking mouse-tilt, a gradient overlay, a separate blurred glow layer, and
 * per-element `translateZ`, while `LocationCard` and `PromoCard` each did something else. Requirement
 * 7.5 is that; these constants are the whole of the answer, and no primitive or app writes an
 * elevation or a ring utility of its own.
 *
 * Every class here is a semantic utility resolving to a token. `verify:tokens` compiles each one
 * through the real preset and rejects a value that is not on a scale, so a treatment cannot quietly
 * acquire a literal.
 */
/**
 * The focus indicator — requirement 7.3's visible half, and where `--ring` is consumed.
 *
 * `focus-visible` rather than `focus`, so a pointer user does not get a ring on every click while a
 * keyboard user always does. `outline-none` removes the browser's own outline only because the ring
 * replaces it; a rule that removed it without a replacement would be the defect this is preventing.
 *
 * The ring is gold in the dark theme and navy in the light one, which is `--ring`'s doing rather than
 * this file's: red cannot reach 3:1 on navy, so a red focus ring would be invisible on the site, and
 * a ring in the primary colour is confusable with a pressed state anyway.
 */
export const focusRing = 'outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2';
/** A surface that sits above the page and does not respond to a pointer. */
export const restingElevation = 'shadow-raised';
/**
 * The one hover-and-elevation treatment for cards.
 *
 * A single step up the elevation ramp on hover, transitioned with the duration and easing tokens, and
 * nothing else: no transform, no glow layer, no gradient. `focus-within` is included because a card
 * containing a focused control is in the same state as a hovered one as far as a reader is concerned,
 * and a keyboard user would otherwise never see the affordance.
 *
 * Reduced motion needs no branch here. The generated stylesheet collapses every duration token to
 * 1ms under `prefers-reduced-motion: reduce` and pins `transition-property` to `opacity`, so the
 * shadow change lands instantly rather than animating — requirement 6.6, handled once in CSS instead
 * of once per component.
 */
export const interactiveElevation = 'shadow-raised transition-shadow duration-base ease-standard hover:shadow-overlay focus-within:shadow-overlay';
/** A floating surface anchored to a trigger: a menu, a popover. */
export const overlayElevation = 'shadow-overlay';
/** A surface that takes over the screen: a dialog. The top of the ramp. */
export const modalElevation = 'shadow-modal';
//# sourceMappingURL=treatments.js.map